import { AnswerSource, AnswerSourceFilter } from 'services/streamInsightsQuery';

// An answer can ground several aggregate_metric queries. We only carry a filter
// through to the report when the whole answer used ONE consistent filter set —
// otherwise the intent is ambiguous (e.g. a Dallas-vs-Houston comparison ran two
// different filters and neither is "the" filter to apply).

// Order-independent identity of a filter set: sort values within a filter, then
// sort filters, then stringify.
const normalise = (filters: AnswerSourceFilter[]): string =>
  JSON.stringify(
    filters
      .map((filter) => ({
        field: filter.field,
        operator: filter.operator,
        values: [...filter.values].map(String).sort(),
      }))
      .sort((a, b) =>
        `${a.field}${a.operator}`.localeCompare(`${b.field}${b.operator}`),
      ),
  );

// Returns the single consistent non-empty filter set to carry through, or null:
//   0 distinct non-empty sets  → null (the answer used no filters)
//   exactly 1                  → that set
//   2 or more                  → null (ambiguous / contradictory)
const detectCarryThroughFilters = (
  sources: AnswerSource[] | undefined,
): AnswerSourceFilter[] | null => {
  const nonEmpty = (sources ?? [])
    .map((source) => source.filters ?? [])
    .filter((filters) => filters.length > 0);

  if (nonEmpty.length === 0) {
    return null;
  }

  const byKey = new Map<string, AnswerSourceFilter[]>();
  nonEmpty.forEach((filters) => byKey.set(normalise(filters), filters));

  return byKey.size === 1 ? [...byKey.values()][0] : null;
};

export default detectCarryThroughFilters;
